import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import { Popover, PopoverAnchor, PopoverPanel, PopoverTrigger } from '@clay-run/glaze/popover';
import type { ActivePlayType, PlayOption } from '@shared/flow/play-catalog';
import { useEnrollQueue } from '../EnrollQueue';
import { PLAY_PICKER_POPOVER_PANEL_CLASS, PlayPicker } from '../EnrollQueue/PlayPicker';

/**
 * Contacts tab bulk-enroll popover (flow-contacts-bulk-send plan, U6). The
 * "Bulk actions (n)" toolbar trigger + a flat "Add N to <play>" list, one
 * row per live play.
 *
 * R14 (v2-qa-followups plan, U5) — real Glaze `Popover`/`PopoverTrigger`/
 * `PopoverPanel`, the exact pattern `shell-next/tables/ContactEnrollTrigger.tsx`
 * uses, with the shared `PlayPicker` (in its `frameless` variant) as the list
 * rather than bespoke button markup.
 *
 * This replaced a hand-rolled `usePortalDropdown` + `createPortal` shell whose
 * ONLY reason to exist was pinning the menu at an inline `zIndex: 60`, because
 * Glaze's `PopoverPanel` used to portal at `z-index: auto` and got painted over
 * by `FlowTable.tsx`'s `sticky z-10` header. That rationale is obsolete:
 * `web/app-home/src/index.css:1359` now pins `body > [data-base-ui-portal]` at
 * `z-index: 60`, above both that sticky header and the `z-50` fallback dock, for
 * every Base UI portal in the app. Do not reintroduce a bespoke portal here.
 *
 * Panel width/padding come from `PLAY_PICKER_POPOVER_PANEL_CLASS`
 * (`EnrollQueue/PlayPicker.tsx`) — shared with `ContactEnrollTrigger`, the
 * other Glaze popover host of this picker; change it there, not here.
 *
 * Dismissal is controlled (`open`/`onOpenChange`) rather than uncontrolled
 * because `canClose()` has to veto Base UI's close requests in two cases: a
 * commit in flight (`committingKey`) and the Gong browse panel being open
 * (`browsePanelOpen`) — see `canClose` below.
 *
 * NEW UI — `PlayPicker.tsx:18-20` explicitly defers multi-select/counts, so
 * this reuses only the catalog itself (`useEnrollQueue().plays`/
 * `activePlayTypes`/`gongFlowsSuggested` — all already fetched by the
 * provider, no second fetch) plus small additive `PlayPicker` extensions
 * (`playRowLabel`/`playRowDisabled`/`addUnassignedLabel`/
 * `onBrowseOpenChange`, D1 + flow-contacts-bulk-send Gong parity) for the
 * bulk-specific per-play copy/gating — none of `PlayPicker`'s own search/
 * Gong-browse/auto-email-confirm machinery is bypassed.
 *
 * Gong flow parity (flow-contacts-bulk-send Gong parity plan,
 * `docs/flow/bulk-add-gong-flow-picker-gap.md`) — this popover used to
 * exclude `gong_flow` entirely on a stale rationale ("the exposure deriver
 * returns one row per mirrored Gong flow, hundreds, an unusable flat list
 * here"). That's no longer true: the catalog payload trim
 * (`play-catalog.ts`) means a `catalog: true` exposure kind like `gong_flow`
 * never enumerates individual rows into `plays` at all, so there was never
 * anything to filter there — the actual exclusion was hiding `PlayPicker`'s
 * own hand-built "Gong Flows — suggested" + "Browse all Gong flows →"
 * section by stripping `gong_flow` out of `activePlayTypes` before it ever
 * reached `PlayPicker`. This popover now passes `plays`/`activePlayTypes`/
 * `gongFlowsSuggested` straight through unfiltered, giving bulk the exact
 * same Gong section the single-contact `PlayPicker` mounts
 * (`QueueBody.tsx`/`ContactActionMenus.tsx`) already have — parity, not a
 * new UI model. Gong eligibility (`eligibleFor` below) can't come from the
 * old per-instance `eligibleForPlay` Map (an ad-hoc Gong `PlayOption` built
 * at click time, `PlayPicker.tsx`'s `buildGongFlowPlayOption`, is never a
 * key in a map precomputed over `plays`) — every Gong `PlayOption`
 * (suggested-row or browse-picked) instead resolves against ONE shared
 * eligible list, since Gong eligibility is type-wide (`requires_email` is a
 * property of the type, not the instance), dispatched on
 * `play.ui?.own_picker_section` — the same capability flag `PlayPicker.tsx`
 * itself uses, not a slug comparison.
 *
 * Strictly play-scoped (decision 15): there is no "Add to Queue
 * (unassigned)" entry here, ever — bulk exists to land a review-ready batch
 * on a specific play's card, and an unassigned bulk-add has nothing to
 * review or send yet. `onAddUnassigned` is deliberately never passed to
 * `PlayPicker` below (D1 Open Question 2 unresolved) — PlayPicker's own
 * `{onAddUnassigned && (...)}` conditional means the row simply never
 * renders.
 *
 * No "Suggest for a play type" path (Reflection fix B4, 2026-09-01): the
 * prior secondary entry called `onCommitted()` and toasted success without
 * ever writing anything — no API call, no queue item, no tag. Removed
 * entirely rather than wired to a real backend (separate scope).
 */

/** Interim flat cap (decision E / R14) — mirrors `SendSchema`'s and
 *  `BulkAddSchema`'s `.max(100)`. Real per-action-type numbers are a named
 *  follow-up; this popover just refuses to commit a batch the server would
 *  reject outright, rather than silently truncating the rep's selection. */
const BULK_ADD_CAP = 100;

const READ_ONLY_MESSAGE = 'Bulk actions are read-only while impersonating';

/**
 * Hosted mode (Signals selection bar): the popover has no trigger of its own — a menu item
 * elsewhere opens it, and it anchors to an invisible `PopoverAnchor` where it is mounted.
 * `playTypeId` narrows the picker to ONE play type's plays (null = every live type).
 * Everything else — eligibility, caps, commit, toasts — is this component's, unchanged.
 */
export interface BulkEnrollPopoverHost {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playTypeId: number | null;
  /** Provenance tag for the bulk-add; defaults to the Contacts toolbar's. */
  sourceContext?: string;
}

export interface BulkEnrollPopoverProps {
  /**
   * The Contacts tab's current bulk selection — owned by `ContactFlowTable`,
   * but ORDERED (U6 scope 2 / Decision 2): the caller projects its selection
   * Set into the current rendered table order (with any selected id no
   * longer loaded appended after), and this component trusts that order
   * verbatim — it never re-sorts. That order is what "first 100" (Decision
   * 16) and the POST body both use.
   */
  selectedIds: readonly string[];
  /**
   * R12 single source of truth for "already queued" (any play) — computed
   * ONCE per render in `ContactFlowTable` from `useEnrollQueue().groups`
   * (the `WorkflowsDropdown` derivation) and passed down, never
   * re-derived here.
   */
  queuedContactIds: ReadonlySet<string>;
  /** Email lookup for the `requires_email` advisory gate, built by the
   *  caller off the rows it already has loaded — this component never
   *  fetches contacts itself. */
  emailByContactId: ReadonlyMap<string, string | null>;
  /**
   * Called after a successful bulk-add commit with the SET of contact ids
   * the response actually reported as `added` (U6 scope 2 / Decision 15) —
   * server-authoritative, never the request's requested ids. The caller
   * prunes only those ids from its selection; skipped and cap-truncated ids
   * are left checked. Never called on a failed commit (selection must be
   * retained for a retry).
   */
  onCommitted: (addedContactIds: ReadonlySet<string>) => void;
  /** Hosted mode: a menu item opens this popover (it has no trigger of its own). Absent, it renders its own trigger. */
  host?: BulkEnrollPopoverHost;
}

/**
 * Play identity everywhere else (the DB unique constraint on
 * `FlowPlayOverride`, `PlayQueueCard`'s lookup, `QueueBody`'s card key,
 * `ContactQueueRow`'s lookup) is the `(play_type_id, play_instance_ref)`
 * composite, not the bare instance ref (Reflection fix B7, 2026-09-01) —
 * today's derivers happen to keep refs disjoint across play types, but two
 * plays sharing an instance ref would otherwise silently overwrite one
 * play's eligible list/committing-state with the other's.
 */
const playKey = (p: PlayOption) => `${p.play_type_id}:${p.play_instance_ref}`;

export function BulkEnrollPopover({ selectedIds, queuedContactIds, emailByContactId, onCommitted, host }: BulkEnrollPopoverProps) {
  const { plays: allPlays, activePlayTypes: allPlayTypes, gongFlowsSuggested: allGongSuggested, bulkAdd, readOnly, readOnlyReason } = useEnrollQueue();
  const hostPlayTypeId = host?.playTypeId ?? null;
  const plays = useMemo(() => (hostPlayTypeId ? allPlays.filter(p => p.play_type_id === hostPlayTypeId) : allPlays), [allPlays, hostPlayTypeId]);
  const activePlayTypes = useMemo(
    () => (hostPlayTypeId ? allPlayTypes.filter(t => t.play_type_id === hostPlayTypeId) : allPlayTypes),
    [allPlayTypes, hostPlayTypeId],
  );
  // The suggested Gong flows only belong to the Gong type's own section.
  const gongFlowsSuggested = useMemo(
    () => (hostPlayTypeId && !activePlayTypes.some(t => t.ui?.own_picker_section) ? [] : allGongSuggested),
    [allGongSuggested, hostPlayTypeId, activePlayTypes],
  );
  const readOnlyMessage = readOnlyReason ?? READ_ONLY_MESSAGE;
  const [committingKey, setCommittingKey] = useState<string | null>(null);
  /**
   * Browse-dismissal guard (Key Technical Decision 3) — `GongFlowBrowse`
   * portals to `document.body` (`GongFlowBrowse.tsx:304`), a DOM SIBLING of
   * this popover's own Base UI portal, not a descendant of it, so every
   * interaction inside it reads as an "outside press" to Base UI. Tracked
   * here via `PlayPicker`'s additive `onBrowseOpenChange` prop so BOTH
   * dismissal paths (`canClose`, consulted from `onOpenChange` for
   * outside-press AND Escape) stay guarded while the panel is open —
   * without this, the first interaction inside the panel would dismiss this
   * popover and unmount the panel with it.
   *
   * `autoEmailConfirmOpen` is the same guard for the auto-email confirm, which
   * `PlayPicker` also portals to `document.body` (see its
   * `onAutoEmailConfirmOpenChange` prop doc).
   */
  const [browsePanelOpen, setBrowsePanelOpen] = useState(false);
  const [autoEmailConfirmOpen, setAutoEmailConfirmOpen] = useState(false);
  const canClose = useCallback(
    () => committingKey === null && !browsePanelOpen && !autoEmailConfirmOpen,
    [committingKey, browsePanelOpen, autoEmailConfirmOpen],
  );
  const [localOpen, setLocalOpen] = useState(false);
  const open = host ? host.open : localOpen;
  const setOpen = host ? host.onOpenChange : setLocalOpen;

  /**
   * Per-play eligible id lists for the (non-Gong) catalog `plays` (R12(b) /
   * the plan's "N = selected − alreadyQueuedAny −
   * missingEmail-for-requiresEmail-types" formula) — computed off
   * `queuedContactIds` (never re-derived) and the caller's email lookup, one
   * pass per play. Keyed on the composite `playKey` (B7).
   *
   * Gong parity (Key Technical Decision 2) — `gong_flow` (any play whose
   * `ui.own_picker_section` is true, dispatched the same way `PlayPicker.tsx`
   * itself does, never on `play_type_slug`) is skipped here: its ad-hoc
   * `PlayOption`s are built at click time (`buildGongFlowPlayOption`) and
   * would never be a key in a map precomputed over `plays` — see
   * `gongEligibleIds`/`eligibleFor` below.
   *
   * U6 scope 2: iterates the caller's ORDERED `selectedIds` list (table
   * order) instead of a Set — the exclusion checks themselves (already-
   * queued / missing-email) are unchanged, only the resulting list's order
   * now matches what the rep sees on screen, which is what "first 100"
   * (Decision 16) slices from.
   */
  const catalogEligibleMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const play of plays) {
      if (play.ui?.own_picker_section) continue;
      const ids: string[] = [];
      for (const id of selectedIds) {
        if (queuedContactIds.has(id)) continue;
        if (play.requires_email && !emailByContactId.get(id)) continue;
        ids.push(id);
      }
      map.set(playKey(play), ids);
    }
    return map;
  }, [plays, selectedIds, queuedContactIds, emailByContactId]);

  /** The live Gong type's kind-marker row (if any) — same lookup `PlayPicker.tsx`'s own `gongCatalogEntry` does off `activePlayTypes`, not `plays`. */
  const gongActiveType: ActivePlayType | undefined = useMemo(
    () => activePlayTypes.find(t => t.ui?.own_picker_section),
    [activePlayTypes],
  );

  /**
   * ONE shared eligible list for every Gong `PlayOption`, suggested-row or
   * browse-picked alike (Key Technical Decision 2) — Gong eligibility is
   * type-wide, not per-flow-instance: `requires_email` lives on the TYPE
   * (`gongActiveType`), identical for every flow under it, so there is
   * nothing per-instance to key a map on.
   */
  const gongEligibleIds = useMemo(() => {
    if (!gongActiveType) return [];
    const ids: string[] = [];
    for (const id of selectedIds) {
      if (queuedContactIds.has(id)) continue;
      if (gongActiveType.requires_email && !emailByContactId.get(id)) continue;
      ids.push(id);
    }
    return ids;
  }, [gongActiveType, selectedIds, queuedContactIds, emailByContactId]);

  /**
   * Single eligibility lookup for `rowLabel`/`rowDisabled`/`handlePick` —
   * replaces the old bare `eligibleForPlay` Map. Dispatches on
   * `play.ui?.own_picker_section` (the capability, matching
   * `PlayPicker.tsx:345` — never a `play_type_slug` comparison) so a future
   * second own-picker-section play type is routed the same way with no new
   * branch here.
   */
  const eligibleFor = useCallback(
    (play: PlayOption): string[] => (play.ui?.own_picker_section ? gongEligibleIds : catalogEligibleMap.get(playKey(play)) ?? []),
    [catalogEligibleMap, gongEligibleIds],
  );

  /**
   * Keeps the popover OPEN with the clicked row in a loading state until the
   * bulk-add response lands (Reflection fix B8, 2026-09-01) — the prior
   * behavior closed the popover BEFORE awaiting the POST, so on a 100-contact
   * batch there was nothing on screen between the click and the landing
   * banner. `bulkAdd` itself awaits the queue invalidation
   * (`EnrollQueueContext.tsx`'s `bulkAddMutation`) before firing the landing
   * + opening the queue tab, so closing here only once it resolves means the
   * sidebar is already landing on the expanded card — no dead frame.
   *
   * U3 (in-flight feedback for browse-picked flows) — a `toast.loading` is
   * fired right after `setCommittingKey`, uniformly regardless of which row
   * (or the browse overlay) triggered this call: a browse-picked flow is
   * usually NOT one of the 3 rendered suggested rows, so the row-decoration
   * approach (`rowLabel`) has nowhere on screen to show "Adding N…" for it.
   * All three `toast.*` calls below share `{ id: key }` so sonner replaces
   * the toast in place rather than stacking a second one.
   */
  const handlePick = async (play: PlayOption) => {
    if (readOnly || committingKey) return;
    const key = playKey(play);
    const eligible = eligibleFor(play);
    if (eligible.length === 0) return;
    // U6 scope 2 (R2/Decision 16): over-cap selections no longer refuse to
    // commit — slice to the first BULK_ADD_CAP of the ORDERED eligible list
    // (table order, by construction of the caller's `selectedIds` prop)
    // before POSTing. R1's server-side `.max(100)` stays the hard backstop.
    const toSend = eligible.slice(0, BULK_ADD_CAP);
    const overCap = eligible.length > BULK_ADD_CAP;
    const loadingLabel = overCap
      ? `Adding first ${BULK_ADD_CAP} of ${eligible.length} to ${play.name}…`
      : `Adding ${eligible.length} to ${play.name}…`;
    setCommittingKey(key);
    toast.loading(loadingLabel, { id: key });
    try {
      const response = await bulkAdd({
        contactIds: toSend,
        playTypeId: play.play_type_id,
        playInstanceRef: play.play_instance_ref,
        sourceContext: host?.sourceContext ?? 'contacts-bulk-toolbar',
      });
      // U6 scope 2 (Decision 15): server-authoritative pruning — only ids
      // the response reports as `added` are handed to the caller. Ids never
      // sent (client-excluded / cap-truncated) and ids the server skipped
      // both stay out of this Set, so the caller's selection retains them.
      const added = new Set(
        response.results.filter(r => r.outcome === 'added').map(r => r.contact_id),
      );
      onCommitted(added);
      toast.success(`Added ${added.size} to ${play.name}`, { id: key });
      setOpen(false); // close only once the server has answered
    } catch {
      // Selection is retained on failure (no `onCommitted()` call) so the
      // rep can retry — no phantom banner (`bulkAdd` only lands/invalidates
      // on success). Popover stays open so the retry happens in place.
      toast.error(`Failed to add to queue — ${play.name}`, { id: key });
    } finally {
      setCommittingKey(null);
    }
  };

  /**
   * Verbatim port of the label logic this popover has always used
   * (D1 — extracted so it can be passed as PlayPicker's `playRowLabel`
   * instead of driving bespoke row markup). Copy/thresholds/pluralization
   * unchanged. Now backed by `eligibleFor` (Gong parity) instead of the
   * removed `eligibleForPlay` Map — same shape, same output, for every
   * non-Gong play; a Gong `PlayOption` (suggested row) resolves through the
   * shared `gongEligibleIds` list instead.
   */
  function rowLabel(play: PlayOption): string {
    const eligible = eligibleFor(play);
    const overCap = eligible.length > BULK_ADD_CAP;
    const isCommitting = committingKey === playKey(play);
    const idleLabel = overCap
      ? `Add first ${BULK_ADD_CAP} of ${eligible.length} to ${play.name}`
      : `Add ${eligible.length} to ${play.name}`;
    const loadingLabel = overCap
      ? `Adding first ${BULK_ADD_CAP} of ${eligible.length} to ${play.name}…`
      : `Adding ${eligible.length} to ${play.name}…`;
    return isCommitting ? loadingLabel : idleLabel;
  }

  /** Verbatim port of the disable logic this popover has always used (D1), now backed by `eligibleFor`. */
  function rowDisabled(play: PlayOption): boolean {
    const eligible = eligibleFor(play);
    return eligible.length === 0 || readOnly || committingKey !== null;
  }

  const count = selectedIds.length;
  const triggerDisabled = readOnly || count === 0;

  return (
    // Controlled: `onOpenChange` vetoes every Base UI close request
    // (outside press, Escape, the trigger's own click) while `canClose()` is
    // false — a commit in flight must keep its loading/retry UI on screen,
    // and the Gong browse panel must survive its own first interaction.
    <Popover
      open={open}
      onOpenChange={next => {
        if (!next && !canClose()) return;
        setOpen(next);
      }}
    >
      {/*
        D3 (flow-contacts-bulk-send design-fidelity plan, item 4) — the
        trigger goes Glaze `primary` while a selection is active, so an
        active bulk selection reads as the one call-to-action on the
        toolbar. DELIBERATE exception to "match the prototype": the
        reference prototype's own toolbar button
        (docs/flow/contacts-bulk-send-prototype.html:378, `.tbtn.tsel`) is
        NOT primary-blue there — Spencer's live-QA note explicitly
        supersedes the prototype on this one point. Do not "fix" this back
        to `secondary` to match the prototype at a future fidelity audit.
      */}
      {host ? <PopoverAnchor /> : (
      <PopoverTrigger
        render={
          <Button
            variant={selectedIds.length > 0 ? 'primary' : 'secondary'}
            size="sm"
            disabled={triggerDisabled}
            title={readOnly ? readOnlyMessage : undefined}
          >
            {selectedIds.length > 0 ? `Bulk actions (${selectedIds.length})` : 'Bulk actions'}
          </Button>
        }
        onClick={() => {
          // Codex adversarial-review fix (D1 regression): the trigger's own
          // toggle must consult the same `committingKey !== null` guard
          // `canClose()` already enforces for outside-click/Escape — a
          // bare `onOpenChange` guard that rejected ANY close request would
          // still let this click through first. Without this, clicking
          // "Bulk actions" mid-commit hid the loading/retry UI.
          if (!canClose()) return;
          setOpen(!open);
        }}
      />
      )}
      <PopoverPanel
        data-testid="bulk-enroll-dropdown"
        positioner={{ align: 'end' }}
        className={PLAY_PICKER_POPOVER_PANEL_CLASS}
      >
        {count > 0 && (
          <>
            {/* Same group-heading style `PlayPicker.tsx:506` uses for its own
                play-type headings, so this reads as one continuous menu. */}
            <Text size="xs" variant="secondary" render={<div />} className="truncate px-2 pt-1.5 pb-1">
              Add to a play
            </Text>
            <PlayPicker
              frameless
              plays={plays}
              activePlayTypes={activePlayTypes}
              gongFlowsSuggested={gongFlowsSuggested}
              onAssign={play => void handlePick(play)}
              disabled={readOnly}
              disabledReason={readOnly ? readOnlyMessage : undefined}
              ariaLabelPrefix="Add to"
              contactHasEmail={true}
              warmIntroAvailable={true}
              playRowLabel={play => rowLabel(play)}
              playRowDisabled={play => rowDisabled(play)}
              onBrowseOpenChange={setBrowsePanelOpen}
              onAutoEmailConfirmOpenChange={setAutoEmailConfirmOpen}
            />
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}
